/**
 * applyOps: a pure function from validated ops and the current scene to the next scene, mirroring MARA's apply
 * rules (mara/nanome/agents.py:290-387): an existing serial with a matching visualization (same kind, size, renderBond
 * and bondScale: is_same_visualization, :190-202) is updated, no match toggles the visualization on (add), a new
 * component is added, hidden flags go through toggle-hidden. The toggle and update semantics themselves are the
 * store's (packages/state/src/store.ts toggleVisualization / updateVisualization, ported from Component.cs:49-143).
 *
 * The Pinia store calls this inside one commit() so the whole prompt is a single undo step.
 */
import { isDefaultAtomistic, visualizationIs } from '@nanome-lite/state/src/presets';
import { filterEntrySerials, type Component } from '@nanome-lite/state/src/types';
import type { ColoringModel, FilterModel, VisualizationModel } from '@nanome-lite/state/src/dto';
import type { Op, UnsupportedOp } from './ops';

export interface ApplyState {
  components: Component[];
  /** Next component serial the store would assign (serials only grow). */
  nextComponentSerial: number;
  view?: { skyboxTheme: string; nightMode: boolean };
}

export interface ApplyResult {
  components: Component[];
  nextComponentSerial: number;
  view?: { skyboxTheme: string; nightMode: boolean };
  /** Camera request for the engine, when a focus op was applied. */
  focus?: { componentSerial?: number; entrySerial?: number };
  /** Filters to evaluate into a selection, when a select op was applied. */
  select?: FilterModel[];
  /** The unsupported ops, for the upsell card. */
  unsupported: UnsupportedOp[];
  /** One line per applied op, for the toast / history. */
  description: string[];
  /** Serials of components created by this call. */
  added: number[];
}

const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

/** MARA is_same_visualization (agents.py:190-202): kind + sizeOption + scale + renderBond + bondScale. */
export function isSameVisualization(a: VisualizationModel, b: VisualizationModel): boolean {
  return a.kind === b.kind
    && (a.size?.sizeOption ?? 'NoSize') === (b.size?.sizeOption ?? 'NoSize')
    && Math.abs((a.size?.scale ?? 0) - (b.size?.scale ?? 0)) < 1e-6
    && (a.renderBond ?? undefined) === (b.renderBond ?? undefined)
    && (a.bondScale ?? undefined) === (b.bondScale ?? undefined);
}

function touch(c: Component): void { c.isDefault = false; }

/** store.toggleVisualization: not contained -> add (default atomistic replaces the existing one, inheriting coloring and hydrogen mode; one per non-atomistic kind); contained -> flip visible. */
function toggleVisualization(c: Component, preset: VisualizationModel): string {
  const incoming: VisualizationModel = { ...clone(preset), visible: true };
  const idx = c.visualizations.findIndex((v) => visualizationIs(v, incoming));
  touch(c);
  if (idx === -1) {
    if (isDefaultAtomistic(incoming)) {
      const existing = c.visualizations.find((v) => isDefaultAtomistic(v));
      if (existing) {
        incoming.coloring = clone(existing.coloring);
        if (existing.renderHydrogen !== undefined) incoming.renderHydrogen = existing.renderHydrogen;
      }
      c.visualizations = c.visualizations.filter((v) => !isDefaultAtomistic(v));
    } else if (incoming.kind !== 'Atomistic' && incoming.kind !== 'Interaction') {
      c.visualizations = c.visualizations.filter((v) => v.kind !== incoming.kind);
    }
    c.visualizations.push(incoming);
    return `added ${incoming.kind} to "${c.name}"`;
  }
  const v = c.visualizations[idx]!;
  c.visualizations[idx] = { ...v, visible: !v.visible };
  return `${v.visible ? 'hid' : 'showed'} ${v.kind} on "${c.name}"`;
}

/** store.updateVisualization: replace the visualization of the same kind (Interaction: same identity). */
function replaceVisualization(c: Component, visualization: VisualizationModel): boolean {
  const idx = visualization.kind === 'Interaction'
    ? c.visualizations.findIndex((v) => visualizationIs(v, visualization))
    : c.visualizations.findIndex((v) => v.kind === visualization.kind);
  if (idx === -1) return false;
  touch(c);
  c.visualizations[idx] = clone(visualization);
  return true;
}

/** MARA per-visualization rule: same shape exists -> update (replace by kind); otherwise -> toggle (add). */
function setVisualization(c: Component, visualization: VisualizationModel): string {
  const same = c.visualizations.find((v) => isSameVisualization(v, visualization));
  if (same && replaceVisualization(c, visualization)) return `updated ${visualization.kind} on "${c.name}"`;
  return toggleVisualization(c, visualization);
}

/** store.setColoring: ElementType forces carbonsOnly false; otherwise inherit carbonsOnly when unset. */
function setColoring(c: Component, kind: VisualizationModel['kind'], coloring: ColoringModel): boolean {
  const v = c.visualizations.find((x) => x.kind === kind);
  if (!v) return false;
  const next: ColoringModel = clone(coloring);
  if (next.kind === 'ElementType') next.carbonsOnly = false;
  else if (next.carbonsOnly === undefined && v.coloring.carbonsOnly !== undefined) next.carbonsOnly = v.coloring.carbonsOnly;
  return replaceVisualization(c, { ...v, coloring: next });
}

/** Apply validated ops to a scene. Pure: the input state is not mutated. */
export function applyOps(ops: Op[], state: ApplyState): ApplyResult {
  const components = state.components.map((c) => ({ ...c, filters: clone(c.filters), visualizations: clone(c.visualizations), entrySerials: [...c.entrySerials] }));
  let nextComponentSerial = state.nextComponentSerial;
  const result: ApplyResult = { components, nextComponentSerial, unsupported: [], description: [], added: [] };
  if (state.view) result.view = { ...state.view };
  const bySerial = (s: number) => components.find((c) => c.serial === s);
  const hide = new Set<number>();
  const show = new Set<number>();

  for (const op of ops) {
    switch (op.op) {
      case 'addComponent': {
        if (!op.filters.length || !op.visualizations.length) break;
        const c: Component = {
          name: op.name,
          filters: clone(op.filters),
          visualizations: clone(op.visualizations).map((v) => ({ ...v, visible: v.visible !== false })),
          serial: nextComponentSerial++,
          hidden: op.hidden ?? false,
          isDefault: false,
          entrySerials: filterEntrySerials(op.filters),
        };
        components.push(c);
        result.added.push(c.serial);
        result.description.push(`added "${c.name}" (${c.visualizations.map((v) => v.kind).join(', ')})`);
        break;
      }
      case 'updateVisualization': {
        const c = bySerial(op.componentSerial);
        if (!c) break;
        if (op.visualization.visible !== false && c.hidden) show.add(c.serial);
        result.description.push(setVisualization(c, op.visualization));
        break;
      }
      case 'toggleVisualization': {
        const c = bySerial(op.componentSerial);
        if (!c) break;
        result.description.push(toggleVisualization(c, op.visualization));
        break;
      }
      case 'setColoring': {
        const c = bySerial(op.componentSerial);
        if (!c) break;
        if (setColoring(c, op.visualizationKind, op.coloring)) result.description.push(`recolored ${op.visualizationKind} on "${c.name}" (${op.coloring.kind})`);
        break;
      }
      case 'setHidden': {
        for (const s of op.componentSerials) { if (!bySerial(s)) continue; if (op.hidden) { hide.add(s); show.delete(s); } else { show.add(s); hide.delete(s); } }
        break;
      }
      case 'removeComponent': {
        const i = components.findIndex((c) => c.serial === op.componentSerial);
        if (i === -1) break;
        result.description.push(`removed "${components[i]!.name}"`);
        components.splice(i, 1);
        hide.delete(op.componentSerial); show.delete(op.componentSerial);
        break;
      }
      case 'focus': {
        result.focus = typeof op.componentSerial === 'number' ? { componentSerial: op.componentSerial } : { entrySerial: op.entrySerial! };
        result.description.push(typeof op.componentSerial === 'number' ? `focused component ${op.componentSerial}` : `focused entry ${op.entrySerial}`);
        break;
      }
      case 'select': {
        result.select = clone(op.filters);
        result.description.push('selected atoms');
        break;
      }
      case 'setBackground': {
        const view = result.view ?? { skyboxTheme: 'default', nightMode: true };
        if (op.theme !== undefined) view.skyboxTheme = op.theme;
        if (op.nightMode !== undefined) view.nightMode = op.nightMode;
        result.view = view;
        result.description.push(`background ${op.theme ?? ''}${op.nightMode !== undefined ? ` night mode ${op.nightMode ? 'on' : 'off'}` : ''}`.trim());
        break;
      }
      case 'unsupported': {
        result.unsupported.push(op);
        break;
      }
    }
  }

  const hidden = [...hide].filter((s) => bySerial(s));
  const shown = [...show].filter((s) => bySerial(s));
  for (const c of components) {
    if (hide.has(c.serial)) c.hidden = true;
    else if (show.has(c.serial)) c.hidden = false;
  }
  if (hidden.length) result.description.push(`hid ${hidden.map((s) => `"${bySerial(s)!.name}"`).join(', ')}`);
  if (shown.length) result.description.push(`showed ${shown.map((s) => `"${bySerial(s)!.name}"`).join(', ')}`);
  result.nextComponentSerial = nextComponentSerial;
  return result;
}
